import React, { createContext, useState, useContext } from "react";
import PropTypes from "prop-types";
import parse from "html-react-parser";
import { DEFAULT_LANGUAGE, DEFAULT_INDUSTRY } from "@src/constants/constants";
import AppUtils from "@src/utils/appUtils";
import {
  charityConstants,
  customerConstants,
  selectedBehaviourConstants,
  userConstants,
} from "@src/constants/DCConstants";

/** @type React.Context<{
    getDynamicReplacement: (key: any, internalReplacements?: {}, defaultValue?: string, plainText?: boolean) => any;
    countSimilarDynamicReplacements: (pattern: any) => {};
    existsDynamicReplacements: (pattern: any) => boolean;
}> */
const DynamicContentContext = createContext();
DynamicContentContext.displayName = "DynamicContentContext";

const useDCContext = () => useContext(DynamicContentContext);

const DynamicContentProvider = ({ children, dynamicContent }) => {
  // const { user } = useAuthContext();
  const [dynamicContentON, setDynamicContentON] = useState(true);
  const [profile, setProfileForDC] = useState(null); // Set via role-based context provider (e.g. OperatorContext)
  const [selectedBehaviour, setSelectedBehaviourForDC] = useState(null); // Set via role-based context provider (e.g. OperatorContext)
  const [contextData, setContextData] = useState(
    dynamicContent.data.dynamic_content,
  );

  const countSimilarDynamicReplacements = (pattern) => {
    if (!contextData) return 0;

    if (typeof pattern === "string") {
      return Math.max(
        ...Object.keys(contextData)
          .filter((key) => key.startsWith(pattern))
          .map((key) => parseInt(key.replace(pattern, ""), 10)),
        0,
      );
    }

    if (pattern instanceof RegExp) {
      const map = {};

      Object.keys(contextData).forEach((key) => {
        const match = key.match(pattern);
        if (match) {
          const cat = match[1]; // e.g. faq__catX
          const q = parseInt(match[2], 10); // qY
          if (!map[cat] || map[cat] < q) {
            map[cat] = q;
          }
        }
      });

      return map; // { "1": 3, "2": 4, ... }
    }
  };

  const existsDynamicReplacements = (pattern) => {
    if (!contextData) return false;

    const matchingKey = Object.keys(contextData).find((key) => {
      if (typeof pattern === "string") {
        return key === pattern;
      }

      if (pattern instanceof RegExp) {
        return pattern.test(key);
      }

      return false;
    });

    return matchingKey ? contextData[matchingKey]?.length > 0 : false;
  };

  const getAllDynamicReplacements = (k) => {
    const key = k.toLowerCase();
    // If dynamic content is disabled and key is not an exception, return as-is
    if (!shouldUseDynamicContent(key)) return key;

    const content = contextData[key];
    return content;
  };

  const getDynamicReplacement = (
    k,
    internalReplacements = {},
    defaultValue = "",
    plainText = false,
  ) => {
    const key = k.toLowerCase();
    // If dynamic content is disabled and key is not an exception, return as-is
    if (!shouldUseDynamicContent(key)) return key;

    const contentOptions = contextData[key];
    // Find the first valid, fully replaced string from dynamic content
    const result = contentOptions
      ? findFirstValidReplacement(contentOptions, internalReplacements) ||
        defaultValue
      : (console.warn("Dynamic Content NOT FOUND for key %o", key),
        defaultValue);

    // Format final output depending on flags and result type
    return formatFinalOutput(result, key, plainText);
  };

  /**
   * Determines if dynamic content should be used for a given key.
   * Allows exceptions like "colors__behaviour".
   */
  const shouldUseDynamicContent = (key) =>
    dynamicContentON || key.includes("colors__behaviour");

  /**
   * Tries to find the first valid content value from a list of dynamic options.
   * Replaces all required placeholders with actual values.
   */
  const findFirstValidReplacement = (contentOptions, internalReplacements) => {
    const shuffled = AppUtils.getShuffledArray(contentOptions);

    for (const { content_value, replacements = [] } of shuffled) {
      let text = String(content_value);

      // Replace all placeholders like {{KEYWORD}} with provided values
      const allReplaced = replacements.every((keyword) => {
        const value = resolveReplacement(keyword, internalReplacements);
        if (value == null) {
          return false;
        }

        const regex = new RegExp(
          `\\{\\{\\s*${keyword.toUpperCase()}\\s*\\}\\}`,
          "g",
        );
        text = text.replace(regex, value);
        return true;
      });

      if (allReplaced) return text;
    }

    return null; // No valid match found
  };

  /**
   * Formats the final output based on whether it's a URL, empty, plain text, or HTML.
   * Optionally renders as JSX using html-react-parser for safety.
   */
  const formatFinalOutput = (result, key, plainText) => {
    // If the result is a valid URL, return it as-is (e.g., for images or links)
    if (AppUtils.isValidHttpUrl(result)) return result;

    // If nothing was resolved
    if (!result) {
      if (plainText) return "";
      return key ? (
        <span className="text-lg text-error">
          <pre>{`{{DC:${key}}}`}</pre>
        </span>
      ) : (
        ""
      );
    }

    // Escape HTML content and render it safely as plain text
    return plainText ? result : <span>{parse(result)}</span>;
  };

  const PREFIX_RESOLVERS = [
    {
      prefixes: ["CHARITY_", "DONATION_"],
      getSource: () =>
        profile?.charities?.length
          ? charityConstants(profile.charities[0])
          : null,
    },
    {
      prefixes: ["COMPANY_", "CUSTOMER_"],
      getSource: () =>
        profile?.customer ? customerConstants(profile.customer) : null,
    },
    {
      prefixes: ["BEHAVIOUR_"],
      getSource: () =>
        selectedBehaviour
          ? selectedBehaviourConstants(selectedBehaviour)
          : null,
    },
    {
      prefixes: ["USER_", "FIRSTNAME", "LASTNAME", "FIRST_NAME", "LAST_NAME"],
      getSource: () =>
        profile?.customer ? userConstants(profile.appUser) : null,
    },
  ];

  const resolveReplacement = (keyword, internalReplacements) => {
    // Check internal replacements first
    if (internalReplacements[keyword] != null) {
      return internalReplacements[keyword];
    }

    // Check prefix-based resolvers
    for (const { prefixes, getSource } of PREFIX_RESOLVERS) {
      if (prefixes.some((prefix) => keyword.startsWith(prefix))) {
        const source = getSource();
        if (source && source[keyword] != null) {
          return source[keyword];
        }
      }
    }
    return null;
  };

  const value = {
    getDynamicReplacement,
    countSimilarDynamicReplacements,
    existsDynamicReplacements,
    getAllDynamicReplacements,
    setDynamicContentON,
    setContextData,
    setProfileForDC,
    setSelectedBehaviourForDC,
  };
  return (
    <DynamicContentContext.Provider value={value}>
      {children}
    </DynamicContentContext.Provider>
  );
};

DynamicContentProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { useDCContext };

export default DynamicContentProvider;
